import type { Dictionary } from '@ngrx/entity';
import type { Uuid } from '@common/types';
import type { NormalizedBlockEntity, DenormalizedBlockEntity } from '@web/types';
import type { BlocksState } from './blocks.reducer';
import type { PageStructureState } from '../page-structure-feature.reducer';

import { createSelector } from '@ngrx/store';

import { selectSectionsEntities, selectAllSections } from '@web/store/page-structure/sections/sections.selectors';
import { selectColumnsEntities, selectAllColumns } from '@web/store/page-structure/columns/columns.selectors';
import { selectTabsEntities } from '@web/store/page-structure/tabs/tabs.selectors';
import { selectGridsEntities, selectAllGrids } from '@web/store/page-structure/grids/grids.selectors';
import { selectWidgetsEntities } from '@web/store/page-structure/widgets/widgets.selectors';
import { createStructureDenormalizer } from '@web/store/page-structure/page-structure/structure-denormalizer';

import { getVariantMapList } from '@web/store/page-structure/grids/grids.models';

import { BLOCKS_FEATURE_KEY, blocksAdapter } from './blocks.reducer';
import { pageStructureState } from '../page-structure-state.selectors';

const { selectAll, selectEntities, selectIds } = blocksAdapter.getSelectors();

// Lookup the 'Blocks' feature state managed by NgRx
export const selectBlocksState = createSelector(
    pageStructureState,
    (state: PageStructureState): BlocksState => state[BLOCKS_FEATURE_KEY]
);

export const selectAllBlocks = createSelector(selectBlocksState, (state: BlocksState) => selectAll(state));

export const selectBlocksEntities = createSelector(selectBlocksState, (state: BlocksState) => selectEntities(state));

export const selectBlocksIds = createSelector(selectBlocksState, (state: BlocksState) => selectIds(state));

export const selectBlockById = (id: Uuid) =>
    createSelector(selectBlocksEntities, (entities: Dictionary<NormalizedBlockEntity>) => entities[id]);

/**
 * One instance for the app, because the reuse it does is stateful: it holds the
 * objects it handed out last time. Correctness does not depend on that history —
 * a cached node is only reused when the entity *and* every child reference match
 * the ones it was built from — so a shared instance is safe even though several
 * subscribers pull on this selector.
 */
const denormalizeStructure = createStructureDenormalizer();

export const selectStructure = createSelector(
    selectBlocksEntities,
    selectSectionsEntities,
    selectColumnsEntities,
    selectTabsEntities,
    selectGridsEntities,
    selectWidgetsEntities,
    selectBlocksIds,
    (blocks, sections, columns, tabs, grids, widgets, blocksIds): DenormalizedBlockEntity[] =>
        denormalizeStructure(blocksIds as string[], { blocks, sections, columns, tabs, grids, widgets })
);

/**
 * One block, assembled. Read off {@link selectStructure} rather than denormalized
 * on its own: the page is already assembled there, and taking the block from it
 * means the caller gets the very same object the page is rendering — where a
 * denormalization of its own handed back a private copy of the whole subtree on
 * every call, and rebuilt it whenever any entity anywhere changed.
 */
export const selectBlockStructureEntityById = (id: Uuid) =>
    createSelector(selectStructure, (structure: DenormalizedBlockEntity[]) =>
        structure.find((block: DenormalizedBlockEntity) => block.id === id)
    );

export const isBlockFirst = (id: Uuid) =>
    createSelector(
        selectBlocksIds,
        selectAllBlocks,
        (blocksIds: Uuid[], blocks: NormalizedBlockEntity[]) => blocksIds[0] === id && blocks.length > 1
    );
export const isBlockLast = (id: Uuid) =>
    createSelector(
        selectBlocksIds,
        selectAllBlocks,
        (blocksIds: Uuid[], blocks: NormalizedBlockEntity[]) => blocksIds[blocks.length - 1] === id && blocks.length > 1
    );

export const selectLastBlockIndex = createSelector(selectBlocksIds, (blocksIds: Uuid[]) => blocksIds.length - 1);

export const selectSectionConnectedToList = createSelector(selectAllSections, (sections) => {
    const nonSpecificSections = sections.filter((item) => item.entitySubType !== 'specific');

    return nonSpecificSections.map((item) => item.id);
});

export const selectSpecificSectionConnectedToList = createSelector(selectAllSections, (sections) => {
    const specificSections = sections.filter((item) => item.entitySubType === 'specific');

    return specificSections.map((item) => item.id);
});

export const selectColumnConnectedToList = createSelector(selectAllSections, selectAllGrids, (sections, grids) => {
    const nonSpecificSections = sections.filter((item) => item.entitySubType !== 'specific');

    const columnIds = nonSpecificSections.flatMap((item) => item.children);
    const gridIds = grids.flatMap((grid) => {
        return Array.from({ length: getVariantMapList(grid.props.variant) }).map((_, index) => {
            return `grid_${grid.id}_${index}`;
        });
    });

    return gridIds.concat(columnIds);
});

export const selectSpecificColumnConnectedToList = createSelector(selectAllSections, (sections) => {
    const specificSections = sections.filter((item) => item.entitySubType === 'specific');

    return specificSections.flatMap((item) => item.children);
});
